/**
 * Pure resolver for composition text fields under a non-original caption
 * language (PLAT-9966).
 *
 * Priority order:
 *   1. Story phrase: `compositionPhrasesByNormalizedSource.get(normalize(currentValue))`
 *   2. Template preset: when `currentValue` (normalized) matches the control's
 *      authored default value (`controlPreset.templateDefaultValue`) AND a
 *      translation for `activeLanguage` is authored.
 *   3. Fallback: return `currentValue` unchanged.
 *
 * The resolver-order is phrase → preset → original; the PLAT-9963 write-time
 * preset lock guarantees a phrase entry never exists for a (sourceText, lang)
 * pair covered by a preset, so the effective specificity is preset > phrase >
 * original regardless of which step matches first.
 *
 * Original-language short-circuit (`isOriginalLang(activeLanguage)`) is the
 * caller's responsibility — the resolver is pure and assumes the caller has
 * already decided it wants a translation.
 */
import { isOriginalLang } from '@src/utility/captionLanguage';

import { normalizePhrase } from './normalizePhrase';

import type { CompositionPhraseTranslation } from './compositionTypes';

export interface ControlPresetSnapshot {
  /** The control's authored default value (`control.default.value`). */
  templateDefaultValue: string;
  /** The control's authored preset map (`control.translations`). */
  translations?: Record<string, string>;
}

export interface ResolveArgs {
  currentValue: string;
  activeLanguage: string;
  compositionPhrasesByNormalizedSource: ReadonlyMap<
    string,
    CompositionPhraseTranslation
  >;
  controlPreset?: ControlPresetSnapshot;
}

export function resolveCompositionFieldText(args: ResolveArgs): string {
  return resolveCompositionFieldTextWithSource(args).value;
}

/**
 * PLAT-10046 — same priority order as `resolveCompositionFieldText` but
 * returns the resolution source alongside the value. The popover uses the
 * source to drive the `Phrase` / `Preset` / `Missing` badge on translatable
 * rows and to decide which footer button (Translate vs Retranslate) to
 * surface.
 *
 *   - `'phrase'`  — story-wide composition phrase hit (step 1).
 *   - `'preset'`  — template preset hit (step 2).
 *   - `'missing'` — no translation resolved; returned `value` is the
 *     `currentValue` unchanged (step 3 fallback). Treated as "uncovered"
 *     by the Translate button visibility check.
 *
 * Original-language is also reported as `'missing'` because the caller
 * shouldn't be invoking this for the original-language row; if it does,
 * the badge surface should reflect "nothing to resolve". Callers gate on
 * `isOriginalLang(activeLanguage)` before calling.
 */
export type ResolvedSource = 'phrase' | 'preset' | 'missing';

export interface ResolvedFieldText {
  value: string;
  source: ResolvedSource;
  /**
   * PLAT-10046 — the composition phrase cache entry that backed the
   * resolution, when `source === 'phrase'`. Lets callers (popover
   * `buildInitialRows`) read pin metadata without re-running the
   * normalized-source lookup. `undefined` for `'preset'` / `'missing'`.
   */
  entry?: CompositionPhraseTranslation;
}

export function resolveCompositionFieldTextWithSource(
  args: ResolveArgs,
): ResolvedFieldText {
  const {
    currentValue,
    activeLanguage,
    compositionPhrasesByNormalizedSource,
    controlPreset,
  } = args;
  if (isOriginalLang(activeLanguage)) {
    return { value: currentValue, source: 'missing' };
  }

  const key = normalizePhrase(currentValue);

  const phraseHit = compositionPhrasesByNormalizedSource.get(key);
  if (phraseHit && typeof phraseHit.translatedValue === 'string') {
    return {
      value: phraseHit.translatedValue,
      source: 'phrase',
      entry: phraseHit,
    };
  }

  if (controlPreset) {
    const presetValue = controlPreset.translations?.[activeLanguage];
    if (
      typeof presetValue === 'string' &&
      presetValue !== '' &&
      typeof controlPreset.templateDefaultValue === 'string' &&
      controlPreset.templateDefaultValue !== '' &&
      normalizePhrase(controlPreset.templateDefaultValue) === key
    ) {
      return { value: presetValue, source: 'preset' };
    }
  }

  return { value: currentValue, source: 'missing' };
}
